import { useState } from 'react';
import { H5, Heading, IconSpinner, ResponsiveImage, AspectRatioWrapper } from '../../components';
import { FormattedMessage } from 'react-intl';
import css from './ListingPage.module.css';
import { createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { useConfiguration } from '../../context/configurationContext';

import { useDispatch, useSelector } from 'react-redux';
import { addToWishlist, removeFromWishlist, wishListListingsSelector } from '../../containers/WishListPage/WishListPage.duck';
import IconCollection from '../../components/IconCollection/IconCollection';

const iconHeart = <IconCollection iconName="outlineHeart" />
const iconHeartFilled = <IconCollection iconName="heartFilled" />


function SectionListings({ listings, routeConfiguration, history }) {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const wishlistListings = useSelector(wishListListingsSelector);
  const [addToWishlistLoading, setIsAddToWishlistLoading] = useState(null);

  const addListingToWishlist = (listing) => {
    dispatch(addToWishlist(listing.id.uuid)).then(() => {
      setIsAddToWishlistLoading(null);
    });
  };

  const removeListingFromWishlist = (listing) => {
    dispatch(removeFromWishlist(listing.id.uuid)).then(() => {
      setIsAddToWishlistLoading(null);
    });
  };

  const handleWishlistClick = (listing, isWishlisted) => {
    if (addToWishlistLoading) return;

    setIsAddToWishlistLoading(listing.id.uuid);
    if (isWishlisted) {
      removeListingFromWishlist(listing);
    } else {
      addListingToWishlist(listing);
    }
  };

  const handleListingClick = listing => {
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        {
          id: listing.id.uuid,
          slug: createSlug(listing.attributes.title),
        },
        {}
      )
    );
  };

  return (
    <div className={css.sectionListings}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.authorListingsTitle" />
      </Heading>

      <div className={css.listingsContainer}>
        {listings.length === 0 ? (
          <div className={css.noListings}>
            <FormattedMessage id="SectionListings.noListings" />
          </div>
        ) : (
          listings.map((listing, index) => {
            const totalRatingSum = listing?.attributes?.publicData?.totalRatingSum;
            const totalRatings = listing?.attributes?.publicData?.totalRatings;
            const averageRating = totalRatings ? Number((totalRatingSum / totalRatings).toFixed(1)) : '0';

            // Get image and variants with fallback logic
            const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;
            const {
              aspectWidth = 1,
              aspectHeight = 1,
              variantPrefix = 'listing-card',
            } = config.layout.listingImage;

            const variants = firstImage
              ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
              : [];

            // Fallback to other available variants if listing-card variants are not available
            const fallbackVariants = firstImage && variants.length === 0
              ? Object.keys(firstImage?.attributes?.variants).filter(k =>
                k.includes('scaled-medium') || k.includes('scaled-small') || k.includes('square-small') || k.includes('landscape-crop')
              )
              : [];

            const finalVariants = variants.length > 0 ? variants : fallbackVariants;



            const title = listing?.attributes?.title;
            const isFeatured = listing?.attributes?.publicData?.isFeatured;
            const price = listing?.attributes?.price?.amount / 100;
            const address = listing?.attributes?.publicData?.location?.address || "";
            const isWishlisted = wishlistListings.some(item => item?.id?.uuid === listing?.id?.uuid);
            const loading = addToWishlistLoading === listing?.id?.uuid;

            return (
              <div
                className={css.listingCard}
                key={index}
              >
                <div className={css.imageContainer}>
                  {isFeatured && <div className={css.featuredBadge}><FormattedMessage id="SectionListings.featured" /></div>}
                  <div className={css.wishlistIcon} onClick={() => handleWishlistClick(listing, isWishlisted)}>
                    {loading ? <IconSpinner /> : isWishlisted ? iconHeartFilled : iconHeart}
                  </div>
              
                    <ResponsiveImage
                      rootClassName={css.root}
                      alt={title}
                      image={firstImage}
                      variants={finalVariants}
                    />
                </div>
                <div className={css.listingDetails}>
                  <div className={css.priceValue}>
                    ${price} <FormattedMessage id="SectionListings.perHour" />
                  </div>

                  <H5 className={css.listingCardTitle}>{title}</H5>

                  <div className={css.address}>
                    <IconCollection iconName="location" />
                    <span>{address}</span>
                  </div>
                  <span className={css.line} />

                  <div className={css.footerCard}>
                    <div className={css.rating}>
                      <IconCollection iconName="solidPinkHeart" />
                      <p>
                        {averageRating}/5 <span>({totalRatings ? totalRatings : 0})</span>
                      </p>
                    </div>
                    <button onClick={() => handleListingClick(listing)} className={css.viewBtn}>
                      <FormattedMessage id="ListingCard.viewDetails" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SectionListings;
